/**
 * usePostBuddySuggest — web-only hook for the Post Buddy flow on own posts.
 *
 * When the member taps the AI Buddy / Robi icon:
 *  - No buddy selected  → opens the Robi info modal (CTA to /choose-ai-buddy).
 *  - Buddy selected     → opens the AI disclaimer modal, then on Continue
 *                         calls aiBuddyService.suggestComment and bumps the
 *                         comment count (FeedContext by default, or a custom
 *                         updater such as GroupContext.updateGroupCommentCount
 *                         when the `updateCommentCount` option is provided).
 *
 * Mobile parity differences (intentional, per product spec):
 *  - Mobile shows a toast error when no buddy is selected; web shows an
 *    informational modal with a choose-buddy CTA.
 *  - Mobile calls suggestComment directly on tap; web shows the disclaimer
 *    modal first, then calls the API on Continue.
 *  - Mobile excludes video posts; web shows the icon on all own posts.
 */
import { useCallback, useRef, useState } from 'react';
import { useFeed } from '@wlb/shared/context/FeedContext';
import aiBuddyService from '@wlb/shared/api/services/aiBuddyService';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { ToastService } from '@wlb/shared/platform/notifier';
import type { FeedPost } from '@wlb/shared/types/post';
import { useCurrentProfile } from '../profile/useCurrentProfile';

export interface UsePostBuddySuggestOptions {
  /**
   * Override the comment-count updater. Defaults to `FeedContext.updateCommentCount`.
   * Group posts pass `GroupContext.updateGroupCommentCount` so the suggestion
   * bumps the count on the group list, not the main feed.
   */
  updateCommentCount?: (postId: string, delta: number) => void;
}

export interface UsePostBuddySuggestResult {
  /** Post currently in the disclaimer flow, or null. */
  disclaimerPost: FeedPost | null;
  /** Whether the no-buddy info modal is open. */
  robiInfoOpen: boolean;
  /** Post id currently being suggested a comment for, or null. */
  robiLoadingPostId: string | null;
  handleRobiPress: (post: FeedPost) => void;
  handleDisclaimerContinue: () => Promise<void>;
  setDisclaimerPost: (post: FeedPost | null) => void;
  setRobiInfoOpen: (open: boolean) => void;
}

export const usePostBuddySuggest = (options?: UsePostBuddySuggestOptions): UsePostBuddySuggestResult => {
  const { t } = useTranslation();
  const profile = useCurrentProfile();
  const { updateCommentCount: feedUpdateCommentCount } = useFeed();
  const updateCommentCount = options?.updateCommentCount ?? feedUpdateCommentCount;

  const [disclaimerPost, setDisclaimerPost] = useState<FeedPost | null>(null);
  const [robiInfoOpen, setRobiInfoOpen] = useState(false);
  const [robiLoadingPostId, setRobiLoadingPostId] = useState<string | null>(null);
  const inFlightRef = useRef(false);

  const handleRobiPress = useCallback(
    (post: FeedPost) => {
      if (inFlightRef.current) {
        ToastService.show({ type: 'info', message: t('home.feed.postBuddy.loading') });
        return;
      }

      const agentId = profile?.followedAiAgent?.agentId;
      const hasBuddy =
        agentId != null && Number.isSafeInteger(Number(agentId)) && Number(agentId) > 0;

      if (!hasBuddy) {
        setRobiInfoOpen(true);
        return;
      }

      setDisclaimerPost(post);
    },
    [profile?.followedAiAgent?.agentId, t],
  );

  const handleDisclaimerContinue = useCallback(async () => {
    const post = disclaimerPost;
    if (!post) return;

    const agentId = profile?.followedAiAgent?.agentId;
    setDisclaimerPost(null);

    const numericAgentId = Number(agentId);
    if (!Number.isSafeInteger(numericAgentId) || numericAgentId <= 0) {
      setRobiInfoOpen(true);
      return;
    }

    const numericPostId = Number(post.id);
    if (!Number.isSafeInteger(numericPostId) || numericPostId <= 0) {
      ToastService.show({ type: 'error', message: t('home.feed.postBuddy.failed') });
      return;
    }

    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setRobiLoadingPostId(post.id);

    try {
      const result = await aiBuddyService.suggestComment(numericPostId, numericAgentId);
      if (result.success) {
        updateCommentCount(post.id, 1);
        ToastService.show({ type: 'success', message: t('home.feed.postBuddy.success') });
      } else {
        ToastService.show({
          type: 'error',
          message: result.message || t('home.feed.postBuddy.failed'),
        });
      }
    } catch {
      ToastService.show({ type: 'error', message: t('home.feed.postBuddy.failed') });
    } finally {
      inFlightRef.current = false;
      setRobiLoadingPostId(null);
    }
  }, [disclaimerPost, profile?.followedAiAgent?.agentId, updateCommentCount, t]);

  return {
    disclaimerPost,
    robiInfoOpen,
    robiLoadingPostId,
    handleRobiPress,
    handleDisclaimerContinue,
    setDisclaimerPost,
    setRobiInfoOpen,
  };
};

export default usePostBuddySuggest;
